import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { SaveBar } from "../../../components/utils/SaveBar";
import ModernEmployeeSelector from "../../../components/utils/Selector/ModernEmployeeSelector";
import { fetchHrSettings, updateHrSettings } from "../../../api/hrSettings";

const emptySettings = {
  website_form_enabled: false,
  digest_hour: 7,
  responsible_user_ids: [],
  responsibles: [],
};

const normalize = (data = {}) => ({
  website_form_enabled: data.website_form_enabled === true,
  digest_hour: Number.isInteger(data.digest_hour) ? data.digest_hour : 7,
  responsible_user_ids: Array.isArray(data.responsible_user_ids)
    ? data.responsible_user_ids
    : [],
  responsibles: Array.isArray(data.responsibles) ? data.responsibles : [],
});

const snapshotOf = (data) =>
  JSON.stringify({
    website_form_enabled: data.website_form_enabled === true,
    digest_hour: Number(data.digest_hour) || 7,
    responsible_user_ids: [...(data.responsible_user_ids || [])].sort((a, b) => a - b),
  });

export const HrSettings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [settings, setSettings] = useState(emptySettings);
  const [savedSnapshot, setSavedSnapshot] = useState(snapshotOf(emptySettings));
  const [pickerOpen, setPickerOpen] = useState(false);

  const applyLoaded = useCallback((data) => {
    const next = normalize(data);
    setSettings(next);
    setSavedSnapshot(snapshotOf(next));
  }, []);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchHrSettings();
      applyLoaded(data);
    } catch {
      toast.error(t("hr.settings.load_failed"));
    } finally {
      setLoading(false);
    }
  }, [applyLoaded, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const hasChanges = useMemo(
    () => snapshotOf(settings) !== savedSnapshot,
    [settings, savedSnapshot]
  );

  const patchSettings = (patch) => {
    setSaveSuccess(false);
    setSaveError(false);
    setSettings((prev) => ({ ...prev, ...patch }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(false);
    try {
      const data = await updateHrSettings({
        website_form_enabled: settings.website_form_enabled === true,
        digest_hour: Number(settings.digest_hour) || 7,
        responsible_user_ids: settings.responsible_user_ids,
      });
      applyLoaded(data);
      setSaveSuccess(true);
      toast.success(t("hr.settings.saved"));
      return true;
    } catch {
      setSaveError(true);
      toast.error(t("hr.settings.save_failed"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const removeResponsible = (userId) => {
    patchSettings({
      responsible_user_ids: settings.responsible_user_ids.filter((id) => id !== userId),
      responsibles: settings.responsibles.filter((user) => user.id !== userId),
    });
  };

  const digestHours = Array.from({ length: 24 }, (_, hour) => hour);

  if (loading) {
    return (
      <DashboardHeader
        title={t("hr.settings.title")}
        subtitle={t("hr.settings.subtitle")}
      >
        <ResourceLoading />
      </DashboardHeader>
    );
  }

  return (
    <>
      <DashboardHeader
        title={t("hr.settings.title")}
        subtitle={t("hr.settings.subtitle")}
      >
      <div className="bg-[#F6F8FB] p-4 pb-28 sm:p-6">
        <div className="mx-auto max-w-3xl space-y-4">
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-slate-900">
                {t("hr.settings.form.title")}
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                {t("hr.settings.form.description")}
              </p>
            </div>
            <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50/50 p-3">
              <input
                type="checkbox"
                checked={settings.website_form_enabled === true}
                onChange={(event) =>
                  patchSettings({ website_form_enabled: event.target.checked })
                }
                className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
              />
              <div>
                <div className="text-sm font-medium text-slate-900">
                  {t("hr.settings.form.toggle")}
                </div>
                <div className="text-sm text-slate-600">
                  {t("hr.settings.form.toggle_hint")}
                </div>
              </div>
            </label>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-slate-900">
                {t("hr.settings.responsibles.title")}
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                {t("hr.settings.responsibles.description")}
              </p>
            </div>

            {settings.responsibles.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 px-4 py-6 text-sm text-slate-500">
                {t("hr.settings.responsibles.empty")}
              </p>
            ) : (
              <ul className="space-y-2">
                {settings.responsibles.map((user) => (
                  <li
                    key={user.id}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50/50 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-slate-900">
                        {user.displayTitle || user.email}
                      </div>
                      {user.email ? (
                        <div className="truncate text-xs text-slate-500">{user.email}</div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeResponsible(user.id)}
                      className="rounded-full p-1.5 text-slate-400 hover:bg-white hover:text-slate-700"
                      title={t("hr.settings.responsibles.remove")}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <PlusIcon className="h-4 w-4" />
              {t("hr.settings.responsibles.add")}
            </button>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-slate-900">
                {t("hr.settings.digest.title")}
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                {t("hr.settings.digest.description")}
              </p>
            </div>
            <label className="block text-sm font-medium text-slate-700">
              {t("hr.settings.digest.hour")}
              <select
                value={settings.digest_hour}
                onChange={(event) =>
                  patchSettings({ digest_hour: parseInt(event.target.value, 10) })
                }
                className="mt-2 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
              >
                {digestHours.map((hour) => (
                  <option key={hour} value={hour}>
                    {String(hour).padStart(2, "0")}:00
                  </option>
                ))}
              </select>
            </label>
          </section>
        </div>
      </div>
      </DashboardHeader>

      {pickerOpen ? (
        <ModernEmployeeSelector
          open
          useDialog
          multiple
          selectedEmployees={settings.responsibles}
          onChange={(employees) => {
            const next = Array.isArray(employees) ? employees : [];
            patchSettings({
              responsibles: next,
              responsible_user_ids: next.map((user) => user.id),
            });
          }}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}

      <SaveBar
        hasChanges={hasChanges}
        isSaving={saving}
        isSuccess={saveSuccess}
        isError={saveError}
        successMessage={t("hr.settings.saved")}
        errorMessage={t("hr.settings.save_failed")}
        onSave={handleSave}
        saveLabel={t("hr.common.save")}
      />
    </>
  );
};
